package dev.lingvo.site.pages.app.settings.billing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.shared.Plan
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.pages.app.settings.rememberWorkspaceAuthorityState
import dev.lingvo.site.state.WorkspacePrivilege
import dev.lingvo.site.state.WorkspaceRequestContext
import dev.lingvo.site.state.authoritySubject
import dev.lingvo.site.state.requestWorkspaceResource
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LSpinner
import dev.lingvo.site.ds.Space
import dev.lingvo.site.pages.app.projects.slug.settings.settingsOperationResult
import kotlinx.coroutines.delay
import dev.lingvo.site.i18n.t

/**
 * Waits for workspace billing after a checkout, without treating this URL as payment evidence.
 * Pending-checkout links and direct navigation reach it too. Only the selected workspace's read
 * may end the wait; a failed read or deadline leaves payment status explicitly unconfirmed.
 */
@Page("/app/settings/billing/confirming")
@Layout(".components.layouts.AppLayout")
@Composable
fun BillingConfirmingPage() {
    val ctx = rememberPageContext()
    val workspaceContext = WorkspaceRequestContext.current()

    var state by rememberWorkspaceAuthorityState(workspaceContext) {
        mutableStateOf<ConfirmState>(ConfirmState.Waiting)
    }

    LaunchedEffect(workspaceContext?.authoritySubject) {
        if (workspaceContext == null) return@LaunchedEffect
        var elapsed = 0
        while (elapsed < TIMEOUT_MS) {
            // A poll that fails is **not** a payment error. The network being briefly unavailable
            // says nothing about whether the money moved, and showing red here would push someone
            // towards paying twice — the exact outcome this page exists to prevent. Back off and
            // keep waiting.
            //
            // **`settingsOperationResult`, never `runCatching`.** `runCatching` catches `Throwable`,
            // which includes the `CancellationException` this effect is torn down with the moment
            // the reader navigates away — so a cancelled poll was indistinguishable from a poll the
            // network lost, and the loop carried on past this check as though it were still alive.
            // No second request follows it today, because the `delay` below is where the swallowed
            // cancellation surfaces instead; the loop is only correct by accident of where its next
            // suspension point happens to be. Rethrowing here stops the poll where it was actually
            // cancelled, and does not depend on that accident holding.
            val plan = settingsOperationResult {
                requestWorkspaceResource(
                    context = workspaceContext,
                    privilege = WorkspacePrivilege.BILLING_READ,
                    currentContext = WorkspaceRequestContext::current,
                    transport = ApiClient::workspaceBilling,
                ).plan
            }.getOrNull()
            if (plan != null && plan != Plan.FREE) {
                state = ConfirmState.Confirmed
                // Billing shows the returned plan/status; this does not identify a paid transaction.
                ctx.router.tryRoutingTo("/app/settings/billing")
                return@LaunchedEffect
            }
            delay(POLL_INTERVAL_MS.toLong())
            elapsed += POLL_INTERVAL_MS
        }
        state = ConfirmState.TimedOut
    }

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        PageHeader(
            t("app.settings.billing.confirming.checking_checkout_status", "Checking checkout status"),
            t("app.settings.billing.confirming.waiting_for_workspace_status", "Waiting for this workspace's billing status to update."),
        )
        when (state) {
            ConfirmState.Waiting -> {
                LSpinner(label = t("app.settings.billing.confirming.checking_checkout_status", "Checking checkout status"))
                LMuted(t("app.settings.billing.confirming.payment_status_unconfirmed", "Payment status is not confirmed."))
                // Said now, not only on timeout: the impulse to pay again arrives during the wait,
                // not after it.
                LAlert(
                    t("app.settings.billing.confirming.do_not_pay_again", "Do not pay again"),
                    t("app.settings.billing.confirming.check_before_another_checkout", "If you already paid, wait for confirmation or check billing before starting another checkout."),
                    variant = LAlertVariant.INFO,
                )
            }
            ConfirmState.Confirmed -> LMuted(t("app.settings.billing.confirming.opening_billing_status", "Opening your billing page…"))
            ConfirmState.TimedOut -> {
                LAlert(
                    t("app.settings.billing.confirming.checkout_status_unconfirmed", "Checkout status is still unconfirmed"),
                    t("app.settings.billing.confirming.check_billing_or_support", "Check billing for the latest plan and payment status, or contact support@lingvo.dev. Do not pay again before checking."),
                    // A deadline proves neither payment success nor payment failure.
                    variant = LAlertVariant.WARNING,
                )
                LButton(
                    t("app.settings.billing.confirming.go_to_billing", "Go to billing"),
                    { ctx.router.tryRoutingTo("/app/settings/billing") },
                    variant = LButtonVariant.SECONDARY,
                )
            }
        }
    }
}

private enum class ConfirmState { Waiting, Confirmed, TimedOut }

/** Every two seconds, for a minute. Past that a human explanation beats more spinning. */
private const val POLL_INTERVAL_MS = 2_000
private const val TIMEOUT_MS = 60_000

